import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import EditIcon from '@material-ui/icons/Edit';
import { CustomTable } from '../../../components';
import {
  COLUMNS,
  EDITABLE_STATUSES,
  EMPTY_TABLE_MESSAGE,
  STATUS_LABELS,
} from '../constants';
import type { AssistedAffiliationStatus, AssistedAffiliationTableProps } from '../types';
import useStyle from '../styles';

const AssistedAffiliationTable = ({ requests, readOnly = false }: AssistedAffiliationTableProps) => {
  const classes = useStyle();

  const chipClassByStatus: Record<AssistedAffiliationStatus, string> = useMemo(() => ({
    DRAFT: classes.chipDraft,
    IN_PROGRESS: classes.chipInProgress,
    PENDING_INFORMATION: classes.chipDraft,
    COMMERCE_CREATED: classes.chipSuccess,
    REQUEST_CANCELLED: classes.chipError,
    CREATION_ERROR: classes.chipError,
  }), [classes]);

  // Referencia estable: ver `CustomTableProps.data`.
  const rows = useMemo(() => requests.map((request) => ({
    id: request.id,
    active: true,
    registryNumber: request.registryNumber,
    createdAt: new Date(request.createdAt).getTime(),
    identification: request.identification,
    commercialName: request.commercialName,
    legalRepresentative: request.legalRepresentative,
    status: (
      <Chip
        label={STATUS_LABELS[request.status]}
        size="small"
        className={`${classes.chip} ${chipClassByStatus[request.status]}`}
      />
    ),
    bhdExecutive: request.bhdExecutive,
    commercialAssignee: request.commercialAssignee,
    actions: (
      <Box display="flex" justifyContent="center">
        <Tooltip title="Ver">
          <IconButton size="small" aria-label={`ver-${request.registryNumber}`}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Editar">
          <span>
            <IconButton
              size="small"
              aria-label={`editar-${request.registryNumber}`}
              disabled={readOnly || !EDITABLE_STATUSES.includes(request.status)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    ),
  })), [requests, readOnly, classes, chipClassByStatus]);

  if (requests.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="h6" gutterBottom>
          {EMPTY_TABLE_MESSAGE}
        </Typography>
      </Box>
    );
  }

  return (
    <CustomTable
      data={rows}
      columns={COLUMNS}
      readOnly={readOnly}
    />
  );
};

export default AssistedAffiliationTable;
